import { apiRequest } from '@/services/client';
import { getApiErrorMessage } from '@/utils/apiError';
import type { AxiosResponse } from '@umijs/max';

export type DownloadedFile = {
  blob: Blob;
  filename: string;
};

type UploadResponseShape =
  | {
      jobId?: string;
      message?: string;
    }
  | null
  | undefined;

export const parseFilenameFromContentDisposition = (
  disposition: string | undefined,
  fallbackFilename: string,
): string => {
  const filenameMatch = disposition?.match(
    /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i,
  );
  const rawFilename =
    (filenameMatch?.[1] || filenameMatch?.[2]) ?? fallbackFilename;

  try {
    return decodeURIComponent(rawFilename);
  } catch (_) {
    return rawFilename;
  }
};

export const downloadEndpointFile = async (
  endpoint: string,
  fallbackFilename: string,
): Promise<DownloadedFile> => {
  try {
    const response = await apiRequest<AxiosResponse<Blob>>(endpoint, {
      method: 'GET',
      responseType: 'blob',
      getResponse: true,
      retry: { retries: 0 },
      useGlobalErrorHandler: false,
    });

    const disposition: string | undefined =
      (response.headers?.['content-disposition'] as string | undefined) ||
      (response.headers as Record<string, string> | undefined)?.[
        'Content-Disposition'
      ];

    const filename = parseFilenameFromContentDisposition(
      disposition,
      fallbackFilename,
    );

    return { blob: response.data, filename };
  } catch (error) {
    throw new Error(getApiErrorMessage(error));
  }
};

export const uploadSingleFile = async (
  endpoint: string,
  file: File,
): Promise<{ jobId?: string; message?: string }> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await apiRequest<UploadResponseShape>(endpoint, {
    method: 'POST',
    data: formData,
    requestType: undefined,
    retry: { retries: 0 },
    useGlobalErrorHandler: true,
  });

  if (!response || typeof response !== 'object') {
    return {};
  }

  return {
    jobId: response.jobId,
    message: response.message,
  };
};
